//© 2025 Kaitlyn Troxell
import React, { useState } from "react";
import { initializeApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import useUsers from "../hooks/useUsers";
import { firebaseConfig1 } from "../firebase";

// Create secondary app to avoid logging out current user
const secondaryApp = initializeApp(firebaseConfig1, "Secondary");
const secondaryAuth = getAuth(secondaryApp);

function ManageUsersModal({ setShowManageUsers, currentUser, projects }) {
  const { users, toggleUserStatus } = useUsers(currentUser);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ 
    email: "", 
    password: "",
    role: "homeowner",
    projectId: null
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddUser = async (e) => {
    e.preventDefault();

    if (!formData.email.trim()) {
      setError("Email is required");
      return;
    }
    if (!formData.password.trim()) {
      setError("Password is required");
      return;
    }
    if (formData.role === "homeowner" && !formData.projectId) {
      setError("Please assign a project to the homeowner");
      return;
    }

    setSaving(true);
    setError("");

    try {
      // Use secondary auth to avoid logging out current admin
      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth,
        formData.email, 
        formData.password
      );
      
      // Add user document to Firestore with active status, role, and project
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email: formData.email,
        active: true,
        role: formData.role,
        projectId: formData.role === "homeowner" ? formData.projectId : null,
        createdAt: new Date()
      });

      // Sign out from secondary auth immediately
      await secondaryAuth.signOut();

      alert("User added successfully!");
      setFormData({ email: "", password: "", role: "homeowner", projectId: null });
      setShowAddForm(false);
    } catch (err) {
      console.error("Error adding user:", err);
      setError(err.message || "Failed to add user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-container manage-users-modal">
        <h3 className="modal-title">Manage Users</h3>

        {error && <div className="form-error">{error}</div>}

        {/* Existing users list */}
        {users.length > 0 && (
          <div className="modal-section">
            <h4 className="modal-subtitle">Current Users</h4>
            <ul className="user-list">
              {users.map((user) => {
                const userProject = projects?.find(p => p.id === user.projectId);
                return (
                  <li key={user.id} className="user-item">
                    <div>
                      <span style={{ fontWeight: '500' }}>{user.email}</span>
                      <span style={{ 
                        marginLeft: '10px', 
                        fontSize: '12px', 
                        color: user.active ? '#28a745' : '#dc3545' 
                      }}>
                        {user.active ? '(Active)' : '(Disabled)'}
                      </span>
                      <span style={{ 
                        marginLeft: '10px', 
                        fontSize: '12px', 
                        color: '#666',
                        fontStyle: 'italic'
                      }}>
                        {user.role === 'admin' ? '[Admin]' : '[Homeowner]'}
                      </span>
                      {userProject && (
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                          Project: {userProject.name}
                        </div>
                      )}
                    </div>
                    <button 
                      onClick={() => toggleUserStatus(user.id, !user.active)}
                      className={user.active ? "modal-cancel-btn" : "save-btn"}
                      style={{ padding: '6px 12px', fontSize: '13px' }}
                    >
                      {user.active ? "Disable" : "Enable"}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {users.length === 0 && !showAddForm && (
          <div className="empty-state" style={{ marginBottom: '20px' }}>
            No users found. Add your first user below.
          </div>
        )}

        {/* Add User Form */}
        {!showAddForm ? (
          <button
            onClick={() => setShowAddForm(true)}
            className="add-user-btn"
            style={{ width: '100%', marginBottom: '15px' }}
          >
            + Add New User
          </button>
        ) : (
          <div className="modal-section">
            <h4 className="modal-subtitle">Add New User</h4>
            <form onSubmit={handleAddUser}>
              <div className="form-group">
                <label className="form-label" htmlFor="email">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="user@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="password">
                  Password <span className="required">*</span>
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Enter a password (min 6 characters)"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="role">
                  User Role <span className="required">*</span>
                </label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="form-select"
                  required
                >
                  <option value="homeowner">Homeowner (View Only)</option>
                  <option value="admin">Admin (Full Access)</option>
                </select>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  Homeowners can only view the calendar. Admins have full access.
                </div>
              </div>

              {formData.role === "homeowner" && (
                <div className="form-group">
                  <label className="form-label" htmlFor="projectId">
                    Assign to Project <span className="required">*</span>
                  </label>
                  <select
                    id="projectId"
                    name="projectId"
                    value={formData.projectId || ""}
                    onChange={handleChange}
                    className="form-select"
                    required
                  >
                    <option value="">Select a project...</option>
                    {projects && projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Homeowner will only see this project's calendar.
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setFormData({ email: "", password: "", role: "homeowner", projectId: null });
                    setError("");
                  }}
                  className="modal-cancel-btn"
                  disabled={saving}
                >
                  Cancel
                </button>

                <button 
                  type="submit" 
                  className="save-btn" 
                  disabled={saving}
                >
                  {saving ? "Adding..." : "Add User"}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="modal-actions" style={{ marginTop: '20px' }}>
          <button
            onClick={() => setShowManageUsers(false)}
            className="modal-cancel-btn"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ManageUsersModal;